import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const { language, t } = useLanguage();

  const seoData = {
    es: {
      title: 'Contacto | PASTORIA SERRALTA - Promotor Inmobiliario Palma de Mallorca',
      description: 'Contacte con PASTORIA SERRALTA. Cno. Son Rapiña 8, Palma de Mallorca. Teléfono: +34 638 28 12 01. Email: info@pastoria-serralta.com. WhatsApp disponible.'
    },
    en: {
      title: 'Contact | PASTORIA SERRALTA - Real Estate Developer Palma de Mallorca',
      description: 'Contact PASTORIA SERRALTA. Cno. Son Rapiña 8, Palma de Mallorca. Phone: +34 638 28 12 01. Email: info@pastoria-serralta.com. WhatsApp available.'
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: t.contact.address,
      value: t.contact.addressValue,
      link: 'https://maps.app.goo.gl/q2kaqCGVZvZ1EuE1A'
    },
    {
      icon: Phone,
      label: t.contact.phone,
      value: t.contact.phoneValue,
      link: 'tel:+34638281201',
      whatsapp: 'https://wa.me/34638281201'
    },
    {
      icon: Mail,
      label: t.contact.email,
      value: t.contact.emailValue,
      link: 'mailto:info@pastoria-serralta.com'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO 
        title={seoData[language].title}
        description={seoData[language].description}
        url={language === 'es' ? '/contacto' : '/en/contact'}
      />
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/image35.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-[#356C8A]/80" />
        </div>
        
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl sm:text-6xl font-bold mb-4" data-testid="contact-title">
            {t.contact.title}
          </h1>
          <p className="text-xl text-gray-200">{t.contact.subtitle}</p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div
                  key={index}
                  className="group bg-[#E8F2F7] rounded-xl p-8 hover:bg-[#C5DEE8] transition-all hover:shadow-lg"
                  data-testid={`contact-card-${index}`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-[#356C8A] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#356C8A] mb-2">
                      {info.label}
                    </h3>
                    <p className="text-gray-700 mb-4">{info.value}</p>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <a
                        href={info.link}
                        target={info.icon === MapPin ? '_blank' : undefined}
                        rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                        className="px-4 py-2 bg-[#356C8A] text-white rounded-lg hover:bg-[#2B5670] transition-colors text-sm font-medium"
                      >
                        {info.icon === MapPin ? 'Ver mapa' : info.icon === Phone ? 'Llamar' : 'Enviar email'}
                      </a>
                      
                      {/* WhatsApp button for phone */}
                      {info.whatsapp && (
                        <a
                          href={info.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-[#25D366] text-white rounded-lg hover:bg-[#1DA851] transition-colors text-sm font-medium flex items-center gap-2"
                          data-testid="whatsapp-button"
                        >
                          <MessageCircle className="h-4 w-4" />
                          WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-[#E8F2F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
            <div className="aspect-w-16 aspect-h-9 h-96">
              <iframe
                src="https://www.google.com/maps?q=Cno.+Son+Rapi%C3%B1a,+8,+07013+Palma,+Illes+Balears,+Spain&output=embed&z=16"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="PASTORIA SERRALTA Location"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;